import { motion } from "framer-motion";
import {
  Layers,
  Zap,
  Layout,
  Music,
  HelpCircle,
  ExternalLink,
} from "lucide-react";
import { Canvas } from "@react-three/fiber";
import { cn } from "./lib/utils";
import { lazy, Suspense, useState } from "react";
const FreelancerProfileCard = lazy(() => import("./components/ui/freelancer-profile-card").then(m => ({ default: m.FreelancerProfileCard })));
const CardStack = lazy(() => import("./components/ui/card-stack").then(m => ({ default: m.CardStack })));
const ProfileCard = lazy(() => import("./components/ui/profile-card").then(m => ({ default: m.ProfileCard })));
const MusicPlayer = lazy(() => import("./components/ui/music-player").then(m => ({ default: m.MusicPlayer })));
const Accordion01 = lazy(() => import("./components/ui/accordion-01-1").then(m => ({ default: m.Accordion01 })));
const TestimonialsSection = lazy(() => import("./components/blocks/testimonials-columns-1").then(m => ({ default: m.TestimonialsSection })));

// Import centralized data
import {
  profileData,
  toolsData,
  projectsData,
  socialLinks,
  musicData,
  testimonialsData,
} from "./data";
import { Menu, X } from "lucide-react";
import { Button } from "./components/ui/button";
import { ShaderPlane } from "./components/ui/background-paper-shaders";
import { TextHoverEffect } from "./components/ui/hover-footer";

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const tools = toolsData.map((tool) => (
    <tool.icon key={tool.id} className="h-4 w-4 text-primary" />
  ));

  return (
    <div className="dark min-h-screen bg-transparent text-foreground selection:bg-primary/30 relative">
      {/* Background Shader */}
      <div className="fixed inset-0 -z-10 bg-[#050505]">
        <Canvas camera={{ position: [0, 0, 1] }}>
          <ShaderPlane position={[0, 0, 0]} color1="#2a1a4a" color2="#0a0a1a" />
        </Canvas>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 z-50 w-full border-b border-white/5 bg-background/50 backdrop-blur-md">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-xl font-bold tracking-tighter"
          >
            01032008
            <span className="text-primary">.xyz</span>
          </motion.span>
          <div className="hidden gap-8 md:flex">
            {[
              { name: "Projects", id: "projects" },
              { name: "About", id: "about" },
              { name: "FAQ", id: "faq" },
              { name: "Contact", id: "contact" }
            ].map((item, i) => (
              <motion.a
                key={item.name}
                href={`#${item.id}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {item.name}
              </motion.a>
            ))}
          </div>
          <a
            href={profileData.connectButton.href}
            target="_blank"
            rel="noreferrer"
            className="hidden md:block"
          >
            <Button variant="outline" size="sm" className="border-white/10">
              {profileData.connectButton.label}
            </Button>
          </a>

          {/* Mobile Menu Toggle */}
          <button
            className="block md:hidden text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu overlay */}
        <div
          className={cn(
            "fixed inset-0 top-16 z-40 h-[calc(100vh-4rem)] w-full bg-background/95 backdrop-blur-xl transition-all duration-300 md:hidden",
            isMenuOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
          )}
        >
          <div className="flex flex-col items-center justify-center h-full gap-8 p-6">
            {[
              { name: "Projects", id: "projects" },
              { name: "About", id: "about" },
              { name: "FAQ", id: "faq" },
              { name: "Contact", id: "contact" }
            ].map((item) => (
              <a
                key={item.name}
                href={`#${item.id}`}
                onClick={() => setIsMenuOpen(false)}
                className="text-4xl font-bold tracking-tighter hover:text-primary transition-colors"
              >
                {item.name}
              </a>
            ))}
            <a
              href={profileData.connectButton.href}
              target="_blank"
              rel="noreferrer"
              className="mt-4 w-full"
            >
              <Button size="lg" className="w-full h-14 text-lg">
                {profileData.connectButton.label}
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative flex pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="z-10"
            >
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-6">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary"></span>
                </span>
                {profileData.available
                  ? "Available for new projects"
                  : "Currently busy"}
              </div>
              <h1 className="text-4xl font-bold leading-tight tracking-tighter md:text-7xl lg:text-8xl text-gradient mb-6">
                Designing with <span className="hidden md:inline"><br /></span>
                <span className="text-primary">Precision.</span>
              </h1>
              <p className="max-w-[600px] text-lg text-muted-foreground md:text-xl mb-8 leading-relaxed">
                {profileData.bio}
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <a
                  href={profileData.contactButton.href}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full sm:w-auto"
                >
                  <Button size="lg" className="w-full sm:w-auto h-12 px-8 font-semibold">
                    {profileData.contactButton.label}
                  </Button>
                </a>
                <div className="flex items-center gap-3 justify-center">
                  {socialLinks.map((social, i) => (
                    <a
                      key={i}
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Visit my ${social.platform} profile`}
                        className="h-12 w-12 rounded-full border border-white/5 hover:bg-white/5"
                      >
                        <social.icon className="h-5 w-5" />
                      </Button>
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative mx-auto lg:ml-auto w-full max-w-sm mt-12 lg:mt-0"
            >
              <div className="absolute -inset-4 bg-linear-to-tr from-primary/20 to-purple-500/20 blur-3xl opacity-50 -z-10"></div>
              <Suspense fallback={<div className="h-[400px] w-full flex items-center justify-center">Loading profile...</div>}>
                <FreelancerProfileCard
                  name={profileData.name}
                  title={profileData.role}
                  avatarSrc={profileData.avatarSrc}
                  bannerSrc={profileData.bannerSrc}
                  rating={profileData.rating}
                  duration={profileData.duration}
                  rate={profileData.rate}
                  tools={tools}
                  onGetInTouch={() =>
                    window.open(profileData.contactButton.href, "_blank")
                  }
                  className="mx-auto w-full"
                />
              </Suspense>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Card Stack */}
      <section id="projects" className="py-20 border-t border-white/5 relative overflow-x-hidden">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10 md:mb-16">
            <div className="bg-primary/10 text-primary px-4 py-1 rounded-full inline-flex items-center gap-2 mb-4 md:mb-6 border border-primary/20 mx-auto w-fit">
              <Layers className="h-4 w-4" />
              <span className="text-[10px] font-bold uppercase tracking-widest">
                Selected Work
              </span>
            </div>
            <h2 className="text-3xl font-bold md:text-5xl tracking-tight mb-4">
              Featured <span className="text-primary italic">Projects</span>
            </h2>
            <p className="text-muted-foreground text-sm md:text-lg max-w-2xl mx-auto px-4">
              A highlight reel of my most technical builds and creative experiments.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <Suspense fallback={<div className="h-[320px] w-full flex items-center justify-center text-muted-foreground animate-pulse">Loading work...</div>}>
              <CardStack
                items={projectsData.slice(0, 3)}
                cardWidth={520}
                cardHeight={320}
                maxVisible={typeof window !== "undefined" && window.innerWidth < 768 ? 3 : 7}
                overlap={typeof window !== "undefined" && window.innerWidth < 768 ? 0.7 : 0.48}
                autoAdvance={true}
                intervalMs={5000}
              />
            </Suspense>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Suspense fallback={<div className="h-[400px] bg-black/20" />}>
        <TestimonialsSection testimonials={testimonialsData} />
      </Suspense>

      {/* Profile & Music Section */}
      <section id="about" className="py-32">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left: Identity Card */}
            <div className="lg:col-span-4 flex justify-center order-1">
              <Suspense fallback={<div className="w-full h-80 bg-white/5 rounded-[28px]" />}>
                <ProfileCard
                  name={profileData.fullName}
                  role={profileData.role}
                  email={profileData.email}
                  avatarSrc={profileData.avatarSrc}
                  glowText={profileData.glowText}
                  className="max-w-[calc(100vw-3rem)]"
                />
              </Suspense>
            </div>

            {/* Center: Music Player */}
            <div className="lg:col-span-5 order-2 lg:order-2">
              <div className="space-y-8">
                <div className="flex items-center gap-4 text-primary">
                  <Music className="h-6 w-6" />
                  <h3 className="text-xl font-bold uppercase tracking-widest opacity-50">
                    Now Playing
                  </h3>
                </div>
                <Suspense fallback={<div className="w-full h-48 bg-black/40 rounded-[2.5rem] animate-pulse" />}>
                  <MusicPlayer
                    title={musicData.title}
                    artist={musicData.artist}
                    coverSrc={musicData.cover}
                    streamUrl={musicData.streamUrl}
                  />
                </Suspense>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-sm italic">
                  "Coding is a rhythmic process. My builds follow a certain
                  cadence, balancing strict logic with artistic flair."
                </p>
              </div>
            </div>

            {/* Right: Info */}
            <div className="lg:col-span-3 space-y-8 order-3">
              {[
                {
                  icon: Zap,
                  title: "Speed First",
                  desc: "Optimized architectures.",
                },
                {
                  icon: Layers,
                  title: "Modern Stack",
                  desc: "Latest in Next.js & Rust.",
                },
                {
                  icon: Layout,
                  title: "Pixel Perfect",
                  desc: "Graceful interfaces.",
                },
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-4 group"
                >
                  <div className="h-10 w-10 rounded-xl bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors border border-white/5">
                    <feature.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {feature.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faq"
        className="py-32 border-t border-white/5 bg-black/20 relative"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <div className="bg-primary/10 text-primary px-4 py-1 rounded-full inline-flex items-center gap-2 mb-6 border border-primary/20 mx-auto w-fit">
                <HelpCircle className="h-4 w-4" />
                <span className="text-[10px] font-bold uppercase tracking-widest">
                  Knowledge Base
                </span>
              </div>
              <h2 className="text-4xl font-bold md:text-5xl tracking-tight mb-4">
                Questions & <span className="text-primary italic">Answers</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Everything you need to know about my process and technical capabilities.
              </p>
            </div>
            {/* Custom styled Accordion */}
            <div className="glass p-8 rounded-[40px] border-white/10">
              <Suspense fallback={<div className="h-64 animate-pulse bg-white/5 rounded-xl" />}>
                <Accordion01 />
              </Suspense>
            </div>
          </div>
        </div>
      </section>

      {/* Text Hover Footer */}
      <div id="contact" className="w-full h-[300px] md:h-[500px] flex flex-col items-center justify-center relative overflow-hidden bg-black/20 border-t border-white/5">
        <TextHoverEffect text={profileData.name.toUpperCase()} />
        <div className="absolute bottom-12 text-center z-10 transition-opacity hover:opacity-100 opacity-60">
          <p className="text-sm font-medium tracking-[0.2em] text-muted-foreground uppercase mb-4 underline decoration-primary/30 underline-offset-8 decoration-2 cursor-default italic">
            Let's work together
          </p>
          <div className="flex gap-6 justify-center">
            <a
              href={`mailto:${profileData.email}`}
              className="text-lg font-bold text-white hover:text-primary transition-all flex items-center gap-2 group"
            >
              {profileData.email}
              <ExternalLink className="h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>

      {/* Final Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
          <p className="text-xs text-muted-foreground tracking-widest uppercase">
            &copy; {new Date().getFullYear()}{" "}
            {profileData.fullName.toUpperCase()} — ALL RIGHTS RESERVED
          </p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {socialLinks.map((social) => (
              <a
                key={social.platform}
                href={social.href}
                className="text-xs font-semibold hover:text-primary transition-colors uppercase tracking-widest"
              >
                {social.platform}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
